"use client";

import { LabelsLine } from "@/components/discover/LabelsLine";
import { ListingImage } from "@/components/discover/ListingImage";
import { Skeleton } from "@/components/ui/skeleton";
import type { LocaleType } from "@/constants/locales";
import type { EventSource } from "@/types/discover";
import { formatCompactEventDates, resolveEventRange } from "@/utils/date";
import { resolveEventHost } from "@/utils/eventHost";
import { getEventTimeZone } from "@/utils/eventTimeZone";
import { createTaxonomyFormatter } from "@/utils/formatTaxonomy";
import { formatDistance, getDistance } from "@/utils/location";
import { getEventPriceInfo } from "@/utils/price";
import { BadgeCheck } from "lucide-react";
import { useTranslations } from "next-intl";

interface EventCardProps {
  event: EventSource;
  locale?: LocaleType;
  userLocation?: { lat: number; lon: number };
  /** When true, image loads with high priority for LCP optimization */
  priority?: boolean;
}

export function EventCard({
  event,
  locale = "en-gb",
  userLocation,
  priority = false,
}: EventCardProps) {
  const t = useTranslations();
  const format = createTaxonomyFormatter(t);
  const host = resolveEventHost(event);
  const dateRange = resolveEventRange({
    startDate: event.start_date,
    endDate: event.end_date,
    startTime: event.start_time,
    endTime: event.end_time,
  });

  const priceInfo = getEventPriceInfo(
    {
      price_list: event.price_list ?? event.mainProducts ?? [],
      optional_price_list:
        event.optional_price_list ?? event.optionalProducts ?? [],
      price_cents: event.price_cents,
      currency: event.currency,
    },
    t,
    locale,
  );
  const extraLabels = [
    ...(event.skill_level ? [format.skillLevel(event.skill_level)] : []),
    ...(event.age_group ? [format.ageGroup(event.age_group)] : []),
  ];
  const activityLabels = event.activities?.map((a) => format.activity(a)) ?? [];
  const combinedLabels = [...activityLabels, ...extraLabels];

  const distanceText =
    userLocation && event.location
      ? formatDistance(
          getDistance(
            userLocation.lat,
            userLocation.lon,
            event.location.lat,
            event.location.lon,
          ),
          locale,
          t,
        )
      : null;

  return (
    <section
      key={event.id}
      className="w-full max-w-full cursor-pointer rounded-sm"
    >
      {event.image_url ? (
        <ListingImage
          src={event.image_url}
          alt={`${event.event_name} cover`}
          priority={priority}
        />
      ) : (
        <Skeleton className="w-full rounded-sm h-48 sm:h-auto sm:aspect-4/3" />
      )}
      <div className="flex flex-col py-4 gap-2">
        <h3 className="line-clamp-2 text-foreground">
          <span className="font-semibold">{event.event_name}</span>
          {host.displayName && (
            <div className="flex items-center gap-1 text-sm">
              <span className="truncate">{host.displayName}</span>
              {host.canShowClubAffordances && (
                <BadgeCheck
                  className="h-4 w-4 text-white shrink-0"
                  fill="text-foreground"
                />
              )}
            </div>
          )}
        </h3>

        <LabelsLine labels={combinedLabels} />
        {distanceText && (
          <p className="text-xs text-muted-foreground">{distanceText}</p>
        )}
        <div className="flex items-center justify-between text-foreground text-sm">
          <span>
            {formatCompactEventDates({
              locale,
              start: dateRange.start,
              end: dateRange.end,
              includeTime: dateRange.includeTime,
              timeZone: getEventTimeZone(event),
            })}
          </span>
          <span className="font-semibold">{priceInfo.display}</span>
        </div>
      </div>
    </section>
  );
}
